import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import {
  FACTORY_CYCLE_DEFS,
  formatNoteName,
  meterHeightFromRms,
  type FilterType,
  type InstrumentSlot,
  type SamplerLfoWave,
  type SamplerModRoute,
  type SamplerModSource,
  type SamplerModTarget,
  type SamplerPreset,
} from "../../../../src/lib/audio";
import { useStoreSelector } from "../../../../src/lib/store";
import {
  useImportedSampleCatalog,
  withCurrentImportedSample,
} from "../hooks/useImportedSampleCatalog";
import { useSamplerPreset } from "../hooks/useSamplerPreset";
import { getLabActions } from "../labActions";
import { startMicCapture, type MicRecorder } from "../lib/micCapture";
import {
  fileFromDrop,
  isSamplerSampleDrag,
  sampleIdFromDrop,
  samplerPresetFromSampleId,
} from "../lib/sampleDrop";
import { resolveSampleRuntime } from "../lib/sampleLibrary";
import { startSamplePreview, type SamplePreviewHandle } from "../lib/samplePreview";
import {
  SAMPLER_PRESET_CATEGORIES,
  samplerCategorySortIndex,
} from "../lib/samplerBank";
import {
  addSliceAt,
  applyExplodeToPreset,
  listSamplerPadRegions,
  resolvePadZone,
  SAMPLER_PAD_BASE_NOTE,
  SAMPLER_PAD_COUNT,
  singleZoneFromSample,
  sortedSlices,
  swapPadAssignments,
} from "../lib/samplerSlices";
import { normalizeLoopRange, normalizeRegion, loopFromSelection } from "../lib/wavePeaks";
import { projectStore } from "../store/projectStore";
import { patchUi, uiStore } from "../store/uiStore";
import { patchTransport, transportStore } from "../store/transportStore";
import { formatOctaveLabel, MusicalKeyboard } from "./MusicalKeyboard";
import { ParamKnob } from "./ParamKnob";
import { SearchableSelect, type SearchableSelectOption } from "./SearchableSelect";
import { SamplerSampleSelect } from "./SamplerSampleSelect";
import { Checkbox } from "./ui/Checkbox";
import { WaveformEditor } from "./wave/WaveformEditor";
import { instrumentPresetLearnPath } from "../controls/instrumentPresetLeaves";

const STORAGE_KEY = "audio-lab:sampler-author-modal";
const PANEL_WIDTH = 1040;
const PANEL_MIN_HEIGHT = 740;
const MAP_PANEL_WIDTH = 220;
/** Pixels of pointer travel before a pad press becomes a chop-swap drag. */
const PAD_DRAG_THRESHOLD_PX = 6;

const FILTERS: FilterType[] = ["lowpass", "highpass", "bandpass"];
const LFO_WAVES: SamplerLfoWave[] = ["sine", "tri", "square", "saw", "snh", "random"];
/** Comfortable root range for sample mapping (C1–C7). */
const ROOT_MIDI_OPTIONS = Array.from({ length: 73 }, (_, i) => 24 + i);
const MOD_SOURCES: SamplerModSource[] = [
  "ampEnv", "modEnv", "lfo", "lfo2", "key", "velocity", "random",
];
const MOD_TARGETS: SamplerModTarget[] = [
  "pitch", "cutoff", "amp", "pan", "filterRes",
  "startOffset", "playbackPos", "loopStart", "loopEnd", "sampleRate",
  "grainSize", "grainDensity", "grainSpray", "stretch",
];

type PanelPos = { left: number; top: number };

type KnobSpec = {
  path: string;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
};

type SelectSpec = {
  path: string;
  label: string;
  options: readonly string[];
};

type SectionSpec = {
  title: string;
  controls: Array<{ kind: "knob"; spec: KnobSpec } | { kind: "select"; spec: SelectSpec }>;
};

const ENGINE_SECTIONS: SectionSpec[] = [
  {
    title: "Filter",
    controls: [
      { kind: "select", spec: { path: "filter.type", label: "Type", options: FILTERS } },
      { kind: "knob", spec: { path: "filter.cutoffHz", label: "Cutoff", min: 40, max: 18000, step: 1, defaultValue: 12000 } },
      { kind: "knob", spec: { path: "filter.resonance", label: "Res", min: 0, max: 1, step: 0.01, defaultValue: 0.15 } },
    ],
  },
  {
    title: "Amp env",
    controls: [
      { kind: "knob", spec: { path: "ampEnv.a", label: "A", min: 0.001, max: 2, step: 0.001, defaultValue: 0.005 } },
      { kind: "knob", spec: { path: "ampEnv.d", label: "D", min: 0.001, max: 2, step: 0.001, defaultValue: 0.15 } },
      { kind: "knob", spec: { path: "ampEnv.s", label: "S", min: 0, max: 1, step: 0.01, defaultValue: 0.85 } },
      { kind: "knob", spec: { path: "ampEnv.r", label: "R", min: 0.001, max: 4, step: 0.001, defaultValue: 0.25 } },
    ],
  },
  {
    title: "Mod env",
    controls: [
      { kind: "knob", spec: { path: "modEnv.a", label: "A", min: 0.001, max: 2, step: 0.001, defaultValue: 0.01 } },
      { kind: "knob", spec: { path: "modEnv.d", label: "D", min: 0.001, max: 2, step: 0.001, defaultValue: 0.2 } },
      { kind: "knob", spec: { path: "modEnv.s", label: "S", min: 0, max: 1, step: 0.01, defaultValue: 0.2 } },
      { kind: "knob", spec: { path: "modEnv.r", label: "R", min: 0.001, max: 4, step: 0.001, defaultValue: 0.2 } },
    ],
  },
  {
    title: "LFO 1",
    controls: [
      { kind: "select", spec: { path: "lfo.wave", label: "Wave", options: LFO_WAVES } },
      { kind: "knob", spec: { path: "lfo.rateHz", label: "Rate", min: 0.01, max: 30, step: 0.01, defaultValue: 4 } },
      { kind: "knob", spec: { path: "lfo.depth", label: "Depth", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
    ],
  },
  {
    title: "LFO 2",
    controls: [
      { kind: "select", spec: { path: "lfo2.wave", label: "Wave", options: LFO_WAVES } },
      { kind: "knob", spec: { path: "lfo2.rateHz", label: "Rate", min: 0.01, max: 30, step: 0.01, defaultValue: 2 } },
      { kind: "knob", spec: { path: "lfo2.depth", label: "Depth", min: 0, max: 1, step: 0.01, defaultValue: 0 } },
    ],
  },
];

const GRANULAR_SECTION: SectionSpec = {
  title: "Granular",
  controls: [
    { kind: "knob", spec: { path: "granular.grainSizeMs", label: "Grain", min: 5, max: 200, step: 1, defaultValue: 40 } },
    { kind: "knob", spec: { path: "granular.density", label: "Density", min: 0.5, max: 32, step: 0.1, defaultValue: 8 } },
    { kind: "knob", spec: { path: "granular.spray", label: "Spray", min: 0, max: 1, step: 0.01, defaultValue: 0.1 } },
    { kind: "knob", spec: { path: "granular.stretch", label: "Stretch", min: 0.1, max: 4, step: 0.01, defaultValue: 1 } },
    {
      kind: "select",
      spec: { path: "granular.window", label: "Window", options: ["hann", "tri", "rect"] },
    },
  ],
};

function loadPos(): PanelPos | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PanelPos;
    if (typeof parsed.left === "number" && typeof parsed.top === "number") {
      return parsed;
    }
  } catch {
    // ignore
  }
  return null;
}

function savePos(pos: PanelPos): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

function defaultPos(): PanelPos {
  const left = Math.max(12, (window.innerWidth - PANEL_WIDTH) / 2);
  return { left, top: 24 };
}

function clampPos(pos: PanelPos, width: number, height: number): PanelPos {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(8, window.innerHeight - Math.min(height, window.innerHeight - 16) - 8);
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}

function seedRegionIfEmpty(preset: SamplerPreset, length: number): SamplerPreset | null {
  if (length <= 0) {
    return null;
  }
  const needsRegion = preset.region.end <= preset.region.start || preset.region.end === 0;
  const needsLoop = preset.loop.end <= preset.loop.start;
  if (!needsRegion && !needsLoop) {
    return null;
  }
  return {
    ...preset,
    region: needsRegion ? { start: 0, end: length } : preset.region,
    loop: {
      ...preset.loop,
      start: needsLoop ? 0 : preset.loop.start,
      end: needsLoop ? length : preset.loop.end,
    },
  };
}

function getPath(preset: SamplerPreset, path: string): unknown {
  const parts = path.split(".");
  let cur: unknown = preset;
  for (const part of parts) {
    if (cur == null || typeof cur !== "object") {
      return undefined;
    }
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function setPath(obj: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split(".");
  let cur: Record<string, unknown> = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    const next = cur[key];
    if (next == null || typeof next !== "object") {
      cur[key] = {};
    }
    cur = cur[key] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]!] = value;
}

function numberAtPath(preset: SamplerPreset, path: string, fallback: number): number {
  const value = getPath(preset, path);
  return typeof value === "number" ? value : fallback;
}

function formatPathValue(path: string, value: number): string {
  if (path.includes("Hz") || path.includes("grainSizeMs") || path.includes("glideMs")) {
    return String(Math.round(value * 1000) / 1000);
  }
  if (path.includes("Env") || path.endsWith(".a") || path.endsWith(".d") || path.endsWith(".r")) {
    return value.toFixed(3);
  }
  return value.toFixed(2);
}

/**
 * Sampler Author — wave + chop/map on top; synth-style engine sections + matrix below.
 */
export function SamplerAuthorModal() {
  const open = useStoreSelector(uiStore, s => s.authorOpen);
  const instrumentSlot = useStoreSelector(uiStore, s => s.instrumentSlot);
  const slot = (instrumentSlot ?? 0) as InstrumentSlot;
  const { isSampler, preset, patch, commit } = useSamplerPreset(slot);
  const bank = useStoreSelector(projectStore, s => s.samplerBank);

  const bankId =
    bank.find(entry => entry.name === preset?.name)?.id
    ?? bank[0]?.id
    ?? "";

  const bankOptions = useMemo((): SearchableSelectOption[] => {
    const ordered = bank.slice().sort((a, b) => {
      const byCat = samplerCategorySortIndex(a.category) - samplerCategorySortIndex(b.category);
      if (byCat !== 0) {
        return byCat;
      }
      return a.name.localeCompare(b.name);
    });
    return ordered.map(item => ({
      value: item.id,
      label: item.name,
      group: item.category,
      hint: item.tags?.join(", "),
    }));
  }, [bank]);

  const categoryFilters = useMemo(
    () => SAMPLER_PRESET_CATEGORIES.map(entry => ({ id: entry.id, label: entry.label })),
    [],
  );

  const panelRef = useRef<HTMLDivElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>(undefined);
  const fileRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<SamplePreviewHandle | null>(null);
  const rafRef = useRef<number | null>(null);
  const presetRef = useRef(preset);
  presetRef.current = preset;

  const [pos, setPos] = useState<PanelPos>(() => loadPos() ?? { left: 12, top: 12 });
  const [dragging, setDragging] = useState(false);
  const [octaveShift, setOctaveShift] = useState(0);
  const [pcm, setPcm] = useState<Float32Array | null>(null);
  const [sampleRate, setSampleRate] = useState(48_000);
  const [playhead, setPlayhead] = useState<number | null>(null);
  const [playing, setPlaying] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedSliceId, setSelectedSliceId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordLevel, setRecordLevel] = useState(0);
  const [recordError, setRecordError] = useState<string | null>(null);
  const micRef = useRef<MicRecorder | null>(null);
  const recordRafRef = useRef<number | null>(null);
  const padDragRef = useRef<{
    from: number;
    note: number;
    startX: number;
    startY: number;
    dragging: boolean;
  } | null>(null);
  const [padDragFrom, setPadDragFrom] = useState<number | null>(null);
  const [padDragOver, setPadDragOver] = useState<number | null>(null);
  const [renderPadsBusy, setRenderPadsBusy] = useState(false);

  const sampleId = preset?.primarySampleId ?? preset?.zones[0]?.sampleId;
  const { imported, refresh: refreshImported } = useImportedSampleCatalog();
  const importedOptions = withCurrentImportedSample(
    imported,
    sampleId,
    preset?.name ?? "Imported",
  );

  const stopPreview = useCallback(() => {
    previewRef.current?.stop();
    previewRef.current = null;
    setPlaying(false);
    setPlayhead(null);
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const tickPlayhead = useCallback(() => {
    const handle = previewRef.current;
    if (!handle || !handle.playing()) {
      setPlaying(false);
      setPlayhead(null);
      rafRef.current = null;
      return;
    }
    setPlayhead(handle.getFrame());
    rafRef.current = requestAnimationFrame(tickPlayhead);
  }, []);

  const playSelection = useCallback(async () => {
    const current = presetRef.current;
    if (!current || !pcm) {
      return;
    }
    stopPreview();
    let audioCtx = getLabActions()?.getAudioContext?.() ?? null;
    if (!audioCtx) {
      audioCtx = new AudioContext();
    }
    if (audioCtx.state === "suspended") {
      await audioCtx.resume();
    }
    const length = pcm.length;
    const region = normalizeRegion(
      current.region.start,
      current.region.end > current.region.start ? current.region.end : length,
      length,
    );
    const loop = normalizeLoopRange(
      current.loop.start || region.start,
      current.loop.end || region.end,
      region.start,
      region.end,
    );
    const loopLen = loop.end - loop.start;
    const twentyMs = Math.max(16, Math.round(sampleRate * 0.02));
    const xfade = current.loop.xfade != null && current.loop.xfade > 0
      ? current.loop.xfade
      : Math.min(twentyMs, Math.floor(loopLen / 4));
    previewRef.current = startSamplePreview(audioCtx, {
      pcm,
      sampleRate,
      startFrame: region.start,
      endFrame: region.end,
      loop: current.loop.enabled,
      loopStart: loop.start,
      loopEnd: loop.end,
      loopXfade: current.loop.enabled ? xfade : 0,
      reverse: current.reverse,
    });
    setPlaying(true);
    rafRef.current = requestAnimationFrame(tickPlayhead);
  }, [pcm, sampleRate, stopPreview, tickPlayhead]);

  function patchPath(path: string, value: unknown): void {
    const current = presetRef.current;
    if (!current) return;
    const next = structuredClone(current) as SamplerPreset;
    if (!next.lfo2) {
      next.lfo2 = { wave: "square", rateHz: 2, depth: 0 };
    }
    setPath(next as unknown as Record<string, unknown>, path, value);
    commit(next);
  }

  useEffect(() => {
    if (!open || !isSampler || !sampleId) {
      setPcm(null);
      return;
    }
    let cancelled = false;
    setLoadError(null);
    void resolveSampleRuntime(sampleId)
      .then(sample => {
        if (cancelled) return;
        if (!sample) {
          setPcm(null);
          setLoadError(
            `Original sample “${sampleId}” is missing — restore with ⌘Z, re-import, or pick another.`,
          );
          return;
        }
        setPcm(sample.pcm);
        setSampleRate(sample.sampleRate);
        const current = presetRef.current;
        if (current) {
          const seeded = seedRegionIfEmpty(current, sample.pcm.length);
          if (seeded) {
            commit(seeded);
          }
        }
      })
      .catch(error => {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error.message : String(error));
          setPcm(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, isSampler, sampleId, commit, imported]);

  useEffect(() => () => {
    stopPreview();
    if (micRef.current) {
      micRef.current.stop();
      micRef.current = null;
      patchTransport({ authorMicRecording: false });
    }
    if (recordRafRef.current != null) {
      cancelAnimationFrame(recordRafRef.current);
    }
  }, [stopPreview]);

  const micRecording = useStoreSelector(transportStore, s => s.micRecording);

  async function toggleRecord(): Promise<void> {
    if (recording && micRef.current) {
      const recorded = micRef.current.stop();
      micRef.current = null;
      setRecording(false);
      patchTransport({ authorMicRecording: false });
      setRecordLevel(0);
      if (recordRafRef.current != null) {
        cancelAnimationFrame(recordRafRef.current);
        recordRafRef.current = null;
      }
      if (recorded.length < 256) {
        setRecordError("Recording too short");
        return;
      }
      let audioCtx = getLabActions()?.getAudioContext?.() ?? null;
      if (!audioCtx) {
        audioCtx = new AudioContext();
      }
      await getLabActions()?.ingestSamplerPcm?.(slot, {
        name: `Record ${new Date().toLocaleTimeString()}`,
        pcm: recorded,
        sampleRate: audioCtx.sampleRate,
        source: "record",
      });
      refreshImported();
      return;
    }

    if (transportStore.getValue().micRecording) {
      setRecordError("Stop transport Record first");
      return;
    }

    setRecordError(null);
    try {
      let audioCtx = getLabActions()?.getAudioContext?.() ?? null;
      if (!audioCtx) {
        await getLabActions()?.enableAudio?.();
        audioCtx = getLabActions()?.getAudioContext?.() ?? null;
      }
      if (!audioCtx) {
        audioCtx = new AudioContext();
      }
      if (audioCtx.state === "suspended") {
        await audioCtx.resume();
      }
      micRef.current = await startMicCapture(audioCtx);
      setRecording(true);
      patchTransport({ authorMicRecording: true });
      const tick = (): void => {
        if (!micRef.current) return;
        setRecordLevel(micRef.current.getLevel());
        recordRafRef.current = requestAnimationFrame(tick);
      };
      recordRafRef.current = requestAnimationFrame(tick);
    } catch (error) {
      setRecordError(error instanceof Error ? error.message : String(error));
      setRecording(false);
      patchTransport({ authorMicRecording: false });
    }
  }

  useEffect(() => {
    if (!open || !isSampler) {
      stopPreview();
    }
  }, [open, isSampler, stopPreview]);

  useEffect(() => {
    if (!open || !isSampler) {
      return;
    }
    setPos(prev => {
      const el = panelRef.current;
      const width = el?.offsetWidth || PANEL_WIDTH;
      const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(loadPos() ?? defaultPos(), width, height);
      savePos(next);
      return next.left === prev.left && next.top === prev.top ? prev : next;
    });
    panelRef.current?.focus();
  }, [open, isSampler]);

  useEffect(() => {
    if (!open || !isSampler) {
      return;
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        close();
      }
    }
    function onResize(): void {
      setPos(prev => {
        const el = panelRef.current;
        const width = el?.offsetWidth || PANEL_WIDTH;
        const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
        const next = clampPos(prev, width, height);
        savePos(next);
        return next;
      });
    }
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("resize", onResize);
      dragCleanupRef.current?.();
      dragCleanupRef.current = undefined;
    };
  }, [open, isSampler, stopPreview]);

  useLayoutEffect(() => {
    if (!open || !isSampler) {
      return;
    }
    setOctaveShift(0);
    refreshImported();
  }, [open, slot, isSampler, refreshImported]);

  function close(): void {
    stopPreview();
    dragCleanupRef.current?.();
    dragCleanupRef.current = undefined;
    getLabActions()?.closeInstrumentAuthor?.();
    patchUi({ authorOpen: false });
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest("button")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }
    dragCleanupRef.current?.();
    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);

    const onMove = (moveEvent: PointerEvent): void => {
      const width = el.offsetWidth || PANEL_WIDTH;
      const height = el.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(
        {
          left: moveEvent.clientX - offsetX,
          top: moveEvent.clientY - offsetY,
        },
        width,
        height,
      );
      savePos(next);
      setPos(next);
    };

    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  const padExportCount = useMemo(
    () => (preset ? listSamplerPadRegions(preset).length : 0),
    [preset],
  );

  if (!open || !isSampler || !preset) {
    return null;
  }

  const length = pcm?.length ?? 0;
  const region = normalizeRegion(
    preset.region.start,
    preset.region.end > 0 ? preset.region.end : length,
    length || 1,
  );

  const sections = preset.playMode === "granular"
    ? [...ENGINE_SECTIONS, GRANULAR_SECTION]
    : ENGINE_SECTIONS;
  const sliceCount = sortedSlices(preset.slices).length;

  async function onRenderPadsToDrums(): Promise<void> {
    if (renderPadsBusy || padExportCount === 0) {
      return;
    }
    setRenderPadsBusy(true);
    try {
      await getLabActions()?.renderSamplerPadsToDrums?.(slot);
    } finally {
      setRenderPadsBusy(false);
    }
  }

  return createPortal(
    <div
      ref={panelRef}
      tabIndex={-1}
      role="dialog"
      aria-label="Sampler author"
      className={`synth-author fixed z-[55] flex h-[min(860px,calc(100vh-24px))] w-[min(1040px,calc(100vw-24px))] max-[720px]:h-[calc(100vh-16px)] max-[720px]:w-[calc(100vw-16px)] flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-xs text-slate-300 shadow-[0_28px_64px_rgba(0,0,0,0.5)] outline-none ${dragging ? "select-none" : ""} ${dragOver ? "ring-2 ring-lab-accent/60" : ""}`}
      style={{ left: pos.left, top: pos.top }}
      onDragOver={event => {
        if (!isSamplerSampleDrag(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={async event => {
        event.preventDefault();
        setDragOver(false);
        const libraryId = sampleIdFromDrop(event);
        if (libraryId) {
          patch(samplerPresetFromSampleId(preset, libraryId));
          void getLabActions()?.ensureSamplerSamples?.(slot);
          refreshImported();
          return;
        }
        const file = fileFromDrop(event);
        if (!file) return;
        await getLabActions()?.importSamplerAudio?.(slot, file);
        refreshImported();
      }}
    >
      <div
        className="synth-author-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-white/8 bg-lab-panel-bar px-[0.7rem] py-2"
        onPointerDown={beginDrag}
      >
        <span className="synth-author-title min-w-0 truncate text-xs font-semibold text-slate-100">
          Sampler author · {preset.name} · I{slot + 1}
        </span>
        <button
          type="button"
          className="synth-panel-close rounded border border-lab-line px-2 py-0.5 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={close}
        >
          Close
        </button>
      </div>

      <div className="synth-author-browser flex shrink-0 items-center gap-[0.45rem] border-b border-white/[0.06] px-[0.55rem] py-[0.45rem]">
        <SearchableSelect
          className="min-w-0 flex-1"
          ariaLabel="Sampler preset browser"
          searchPlaceholder="Search name, category, or tag"
          emptyMessage="No presets match."
          value={bankId}
          options={bankOptions}
          filters={categoryFilters}
          onChange={id => getLabActions()?.applySamplerBankEntry?.(slot, id)}
        />
        <div className="flex shrink-0 gap-[0.3rem]">
          <button
            type="button"
            className="rounded border border-lab-line bg-lab-panel-deep px-2 py-[0.28rem] text-[11px] text-slate-200"
            onClick={() => getLabActions()?.saveSamplerBank?.(slot)}
          >
            Save
          </button>
          <button
            type="button"
            className="rounded border border-lab-line bg-lab-panel-deep px-2 py-[0.28rem] text-[11px] text-slate-200"
            onClick={() => getLabActions()?.saveSamplerBankAs?.(slot)}
          >
            Save as…
          </button>
        </div>
      </div>

      <div className="synth-author-browser flex shrink-0 flex-wrap items-center gap-2 border-b border-white/[0.06] px-[0.55rem] py-[0.45rem]">
        <SamplerSampleSelect
          value={sampleId ?? ""}
          factory={FACTORY_CYCLE_DEFS}
          imported={importedOptions}
          className="basis-[10rem]"
          onOpen={() => refreshImported()}
          onChange={nextId => patch(samplerPresetFromSampleId(preset, nextId))}
        />
        <button
          type="button"
          className="rounded border border-lab-line px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => fileRef.current?.click()}
        >
          Import…
        </button>
        <button
          type="button"
          className="rounded border border-lab-line px-2 py-1 text-[11px] text-lab-muted hover:bg-white/[0.06] hover:text-slate-200"
          onClick={() => getLabActions()?.resetInstrumentSlot?.(slot)}
        >
          Reset
        </button>
        <button
          type="button"
          className={[
            "rounded border px-2 py-1 text-[11px]",
            recording
              ? "border-red-400/60 bg-red-500/20 text-red-100"
              : "border-lab-line text-lab-muted hover:bg-white/[0.06] hover:text-slate-200",
          ].join(" ")}
          disabled={micRecording && !recording}
          title={micRecording && !recording ? "Stop transport Record first" : undefined}
          onClick={() => void toggleRecord()}
        >
          {recording ? "Stop rec" : "Record"}
        </button>
        {recording && (
          <span className="inline-flex h-2 w-16 overflow-hidden rounded bg-black/40">
            <span
              className="h-full bg-red-400 transition-[width] duration-75"
              style={{ width: `${Math.round(meterHeightFromRms(recordLevel) * 100)}%` }}
            />
          </span>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="audio/*,.wav,.mp3,.ogg,.flac,.aif,.aiff"
          className="hidden"
          onChange={async event => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (!file) return;
            await getLabActions()?.importSamplerAudio?.(slot, file);
            refreshImported();
          }}
        />
        <button
          type="button"
          className="rounded border border-lab-line px-2 py-1 text-[11px] text-slate-200 hover:bg-white/[0.06]"
          onClick={() => (playing ? stopPreview() : void playSelection())}
        >
          {playing ? "Stop" : "Preview"}
        </button>
        <label className="flex items-center gap-1.5 text-[11px] text-lab-muted">
          <Checkbox
            checked={preset.loop.enabled}
            onCheckedChange={enabled => {
              if (enabled === true) {
                patch({
                  loop: loopFromSelection(region, sampleRate, preset.loop),
                });
                return;
              }
              patch({ loop: { ...preset.loop, enabled: false } });
            }}
            aria-label="Loop"
          />
          Loop
        </label>
        {preset.loop.enabled && (
          <label className="flex items-center gap-1.5 text-[11px] text-lab-muted">
            Loop exit
            <select
              className="rounded border border-lab-line bg-lab-panel px-1.5 py-0.5 text-[11px] text-slate-200"
              value={preset.loop.mode ?? "untilRelease"}
              aria-label="Loop exit mode"
              title="Continuous keeps looping into amp release. Until release plays the post-loop tail."
              onChange={event =>
                patch({
                  loop: {
                    ...preset.loop,
                    mode: event.target.value === "continuous" ? "continuous" : "untilRelease",
                  },
                })}
            >
              <option value="continuous">Continuous</option>
              <option value="untilRelease">Until release</option>
            </select>
          </label>
        )}
        <label className="flex items-center gap-1.5 text-[11px] text-lab-muted">
          <Checkbox
            checked={preset.reverse}
            onCheckedChange={reverse => patch({ reverse: reverse === true })}
            aria-label="Reverse"
          />
          Reverse
        </label>
        <label className="flex items-center gap-1.5 text-[11px] text-lab-muted">
          Mode
          <select
            className="rounded border border-lab-line bg-lab-panel px-1.5 py-0.5 text-[11px] text-slate-200"
            value={preset.playMode}
            onChange={event =>
              patch({ playMode: event.target.value === "granular" ? "granular" : "classic" })}
          >
            <option value="classic">Classic</option>
            <option value="granular">Granular</option>
          </select>
        </label>
        {preset.playMode === "classic" && (
          <label
            className="flex items-center gap-1.5 text-[11px] text-lab-muted"
            title="Pitch notes without changing one-shot duration (granular OLA at stretch 1)"
          >
            <Checkbox
              checked={preset.preserveDuration === true}
              onCheckedChange={v => patch({ preserveDuration: v === true })}
              aria-label="Keep length"
            />
            Keep length
          </label>
        )}
        {preset.zones.length === 1 && preset.zones[0] ? (
          <label className="flex items-center gap-1.5 text-[11px] text-lab-muted" title="MIDI note where the sample plays at original pitch">
            Root
            <select
              className="rounded border border-lab-line bg-lab-panel px-1.5 py-0.5 text-[11px] text-slate-200"
              value={preset.zones[0].rootKey}
              aria-label="Sample root note"
              onChange={event => {
                const rootKey = Number(event.target.value);
                const zone = preset.zones[0]!;
                patch({ zones: [{ ...zone, rootKey }] });
              }}
            >
              {ROOT_MIDI_OPTIONS.map(midi => (
                <option key={midi} value={midi}>{formatNoteName(midi)}</option>
              ))}
              {!ROOT_MIDI_OPTIONS.includes(preset.zones[0].rootKey) && (
                <option value={preset.zones[0].rootKey}>
                  {formatNoteName(preset.zones[0].rootKey)}
                </option>
              )}
            </select>
          </label>
        ) : (
          <span className="text-[10px] text-lab-muted" title="Exploded pads use each pad’s own root">
            Root · per pad
          </span>
        )}
      </div>

      <div className="flex min-h-0 flex-1 overflow-hidden bg-[radial-gradient(120%_80%_at_50%_0%,rgba(61,139,253,0.05),transparent_55%),var(--panel)]">
        <div className="synth-author-scroll flex min-h-0 min-w-0 flex-1 flex-col gap-[0.65rem] overflow-auto px-[0.65rem] pt-[0.55rem] pb-3">
          {loadError && (
            <p className="rounded border border-rose-400/30 bg-rose-500/10 px-2 py-1.5 text-[11px] text-rose-200">
              {loadError}
            </p>
          )}
          {recordError && <p className="text-[11px] text-red-300">{recordError}</p>}

          <label className="flex items-center gap-2 text-[11px] tracking-[0.04em] text-slate-500 uppercase">
            <span>Name</span>
            <input
              type="text"
              className="min-w-0 flex-1 rounded-md border border-white/12 bg-white/[0.04] px-2 py-[0.35rem] text-xs tracking-normal text-slate-200 normal-case [font:inherit]"
              value={preset.name}
              onChange={event => patch({ name: event.target.value })}
            />
          </label>

          <WaveformEditor
            pcm={pcm}
            sampleRate={sampleRate}
            region={region}
            loop={{
              enabled: preset.loop.enabled,
              start: preset.loop.enabled
                ? (preset.loop.start || region.start)
                : region.start,
              end: preset.loop.enabled
                ? (preset.loop.end > preset.loop.start ? preset.loop.end : region.end)
                : region.end,
            }}
            reverse={preset.reverse}
            playhead={playhead}
            slices={preset.slices}
            selectedSliceId={selectedSliceId}
            onRegionChange={next => {
              // Single-sample instruments: keep the one zone in sync so notes
              // hear the Author selection (zone.region wins over preset.region).
              if (preset.zones.length === 1 && preset.zones[0]) {
                patch({
                  region: next,
                  zones: [{ ...preset.zones[0], region: next }],
                });
                return;
              }
              patch({ region: next });
            }}
            onLoopChange={next => patch({ loop: { ...preset.loop, ...next } })}
            onSlicesChange={next => patch({ slices: next.length > 0 ? next : undefined })}
            onSelectSlice={setSelectedSliceId}
          />

          <div className="flex flex-wrap gap-[0.55rem]">
            {sections.map(section => (
              <section
                key={section.title}
                className="synth-author-section w-max max-w-full rounded-lg border border-white/[0.07] bg-black/14 px-2 pt-[0.45rem] pb-[0.55rem]"
              >
                <h3 className="mb-[0.45rem] text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
                  {section.title}
                </h3>
                <div className="flex flex-wrap items-start gap-x-[0.7rem] gap-y-[0.55rem]">
                  {section.controls.map(control =>
                    control.kind === "knob" ? (
                      <ParamKnob
                        key={control.spec.path}
                        label={control.spec.label}
                        min={control.spec.min}
                        max={control.spec.max}
                        step={control.spec.step}
                        value={numberAtPath(preset, control.spec.path, control.spec.defaultValue)}
                        defaultValue={control.spec.defaultValue}
                        density="compact"
                        size={44}
                        format={v => formatPathValue(control.spec.path, v)}
                        learnPath={instrumentPresetLearnPath(slot, control.spec.path)}
                        onChange={v => patchPath(control.spec.path, v)}
                      />
                    ) : (
                      <label key={control.spec.path} className="flex min-w-[4.2rem] flex-col items-center gap-[0.28rem]">
                        <span className="text-center text-[10px] leading-tight tracking-[0.04em] text-slate-500 uppercase">
                          {control.spec.label}
                        </span>
                        <select
                          className="h-8 w-[4.6rem] rounded-md border border-white/12 bg-white/[0.04] px-1 text-[11px] text-slate-200 [font:inherit]"
                          value={String(getPath(preset, control.spec.path) ?? control.spec.options[0] ?? "")}
                          onChange={event => patchPath(control.spec.path, event.target.value)}
                        >
                          {control.spec.options.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </label>
                    ),
                  )}
                </div>
              </section>
            ))}
          </div>

          <AuthorRoutes
            slot={slot}
            routes={preset.routes}
            onChange={routes => patch({ routes })}
          />
        </div>

        <aside
          className="flex shrink-0 flex-col gap-2 overflow-auto border-l border-white/[0.06] bg-black/10 px-2 py-2"
          style={{ width: MAP_PANEL_WIDTH }}
          aria-label="Chop and pad map"
        >
          <h3 className="m-0 text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
            Chop / map
          </h3>
          <div className="flex flex-col gap-1">
            <button
              type="button"
              className="rounded border border-lab-line px-2 py-1.5 text-left text-[11px] text-slate-200 hover:bg-white/[0.06]"
              onClick={() => {
                const frame = playhead ?? region.start;
                const next = addSliceAt(preset.slices, frame);
                patch({ slices: next });
                const added = next.find(s => Math.abs(s.frame - Math.round(frame)) <= 1);
                if (added) setSelectedSliceId(added.id);
              }}
            >
              Add slice
            </button>
            <button
              type="button"
              className="rounded border border-lab-line px-2 py-1.5 text-left text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
              disabled={!pcm || sliceCount === 0}
              onClick={() => {
                if (!pcm) return;
                commit(applyExplodeToPreset(preset, pcm.length));
                setSelectedSliceId(null);
              }}
            >
              Explode to pads
            </button>
            <button
              type="button"
              className="rounded border border-lab-line px-2 py-1.5 text-left text-[11px] text-lab-muted hover:bg-white/[0.06]"
              onClick={() => {
                if (!pcm || !sampleId) return;
                const keptRoot = preset.zones[0]?.rootKey ?? 60;
                commit({
                  ...preset,
                  slices: undefined,
                  zones: singleZoneFromSample(sampleId, pcm.length, keptRoot),
                  loop: { ...preset.loop, enabled: sampleId.startsWith("factory/") },
                });
                setSelectedSliceId(null);
              }}
            >
              Reset map
            </button>
            <button
              type="button"
              className="rounded border border-lab-line px-2 py-1.5 text-left text-[11px] text-slate-200 hover:bg-white/[0.06] disabled:opacity-40"
              disabled={padExportCount === 0 || renderPadsBusy}
              title="Export each filled pad as a library sample, create a drum kit, and add a drums track"
              onClick={() => void onRenderPadsToDrums()}
            >
              {renderPadsBusy ? "Rendering…" : "Render pads → drums"}
            </button>
          </div>

          <p className="m-0 text-[10px] text-lab-muted">
            {preset.zones.length} zone{preset.zones.length === 1 ? "" : "s"}
            {preset.zones.some(z => z.padIndex != null) ? " · pads → C4+" : ""}
            {sliceCount > 0 ? ` · ${sliceCount} slices` : ""}
          </p>

          <div className="grid grid-cols-4 gap-1" data-pad-grid="">
            {Array.from({ length: SAMPLER_PAD_COUNT }, (_, i) => {
              const zone = resolvePadZone(preset.zones, i);
              const note = SAMPLER_PAD_BASE_NOTE + i;
              const noteLabel = formatNoteName(note);
              const isSource = padDragFrom === i;
              const isOver = padDragOver === i && padDragFrom !== null && padDragFrom !== i;
              return (
                <button
                  key={i}
                  type="button"
                  data-pad-index={i}
                  className={[
                    "aspect-square touch-none rounded border px-0.5 font-mono text-[9px] leading-none tracking-tight",
                    zone
                      ? "border-lab-accent/50 bg-[rgba(61,139,253,0.16)] text-slate-100"
                      : "border-lab-line bg-transparent text-lab-muted",
                    isSource ? "opacity-50" : "",
                    isOver ? "ring-2 ring-lab-accent/70" : "",
                  ].join(" ")}
                  title={
                    zone
                      ? `Pad ${i + 1} · ${noteLabel} · ${zone.region?.start ?? 0}–${zone.region?.end ?? "?"} · drag to swap`
                      : `Empty · ${noteLabel} · drag onto / from other pads`
                  }
                  onPointerDown={event => {
                    if (event.button !== 0) {
                      return;
                    }
                    event.preventDefault();
                    event.stopPropagation();
                    getLabActions()?.instrumentNoteOn?.(slot, note, 0.9);
                    padDragRef.current = {
                      from: i,
                      note,
                      startX: event.clientX,
                      startY: event.clientY,
                      dragging: false,
                    };
                    const onMove = (moveEvent: PointerEvent): void => {
                      const drag = padDragRef.current;
                      if (!drag) {
                        return;
                      }
                      const dx = moveEvent.clientX - drag.startX;
                      const dy = moveEvent.clientY - drag.startY;
                      if (
                        !drag.dragging
                        && dx * dx + dy * dy >= PAD_DRAG_THRESHOLD_PX * PAD_DRAG_THRESHOLD_PX
                      ) {
                        drag.dragging = true;
                        getLabActions()?.instrumentNoteOff?.(slot, drag.note);
                        setPadDragFrom(drag.from);
                      }
                      if (!drag.dragging) {
                        return;
                      }
                      const el = document.elementFromPoint(moveEvent.clientX, moveEvent.clientY);
                      const padEl = el?.closest?.("[data-pad-index]") as HTMLElement | null;
                      const over = padEl ? Number(padEl.dataset.padIndex) : NaN;
                      setPadDragOver(Number.isInteger(over) ? over : null);
                    };
                    const onUp = (upEvent: PointerEvent): void => {
                      window.removeEventListener("pointermove", onMove);
                      window.removeEventListener("pointerup", onUp);
                      window.removeEventListener("pointercancel", onUp);
                      const drag = padDragRef.current;
                      padDragRef.current = null;
                      setPadDragFrom(null);
                      setPadDragOver(null);
                      if (!drag) {
                        return;
                      }
                      if (!drag.dragging) {
                        getLabActions()?.instrumentNoteOff?.(slot, drag.note);
                        return;
                      }
                      const el = document.elementFromPoint(upEvent.clientX, upEvent.clientY);
                      const padEl = el?.closest?.("[data-pad-index]") as HTMLElement | null;
                      const to = padEl ? Number(padEl.dataset.padIndex) : NaN;
                      if (!Number.isInteger(to) || to === drag.from) {
                        return;
                      }
                      const current = presetRef.current;
                      if (!current) {
                        return;
                      }
                      commit({
                        ...current,
                        zones: swapPadAssignments(current.zones, drag.from, to),
                      });
                    };
                    window.addEventListener("pointermove", onMove);
                    window.addEventListener("pointerup", onUp);
                    window.addEventListener("pointercancel", onUp);
                  }}
                >
                  {noteLabel}
                </button>
              );
            })}
          </div>

          <p className="m-0 text-[10px] leading-snug text-lab-muted">
            Double-click the wave to drop slices. Right-click a marker to omit its
            interval from explode
          </p>
        </aside>
      </div>

      <div className="synth-author-keys shrink-0 border-t border-white/8 bg-lab-panel-bar px-[0.55rem] pt-1.5 pb-2">
        <div className="mb-1 flex items-center justify-between text-[10px] text-lab-muted">
          <span>Audition · {formatOctaveLabel(octaveShift)} · Preview leaves the pattern playhead alone</span>
        </div>
        <MusicalKeyboard
          key={slot}
          focusHostRef={panelRef}
          baseMidi={60}
          octaves={3}
          voiceMode="poly"
          onNoteOn={(note, velocity) => getLabActions()?.instrumentNoteOn?.(slot, note, velocity)}
          onNoteOff={note => getLabActions()?.instrumentNoteOff?.(slot, note)}
          onOctaveChange={setOctaveShift}
        />
      </div>
    </div>,
    document.body,
  );
}

function AuthorRoutes({
  slot,
  routes,
  onChange,
}: {
  slot: InstrumentSlot;
  routes: SamplerModRoute[];
  onChange: (routes: SamplerModRoute[]) => void;
}) {
  return (
    <div className="synth-author-routes rounded-lg border border-white/[0.07] bg-black/14 px-[0.55rem] pt-[0.45rem] pb-[0.55rem]">
      <div className="mb-1.5 flex items-center justify-between gap-2">
        <h3 className="m-0 text-[10px] font-semibold tracking-[0.06em] text-lab-muted uppercase">
          Mod matrix
        </h3>
        <div className="flex items-center gap-2">
          {routes.length > 0 && (
            <button
              type="button"
              className="px-[0.45rem] py-0.5 text-[11px] text-lab-muted"
              onClick={() => onChange([])}
            >
              Clear
            </button>
          )}
          <button
            type="button"
            className="px-[0.45rem] py-0.5 text-[11px]"
            onClick={() =>
              onChange([...routes, { source: "modEnv", target: "cutoff", depth: 1 }])
            }
          >
            + Route
          </button>
        </div>
      </div>
      {routes.length === 0 && (
        <p className="mb-1 text-[10px] text-lab-muted">No routes — add one to modulate.</p>
      )}
      <div className="flex flex-col gap-[0.3rem]">
        {routes.map((route, index) => (
          <div key={index} className="flex flex-wrap items-center gap-[0.35rem]">
            <select
              className={authorRouteControl}
              value={route.source}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, source: event.target.value as SamplerModSource } : r,
                );
                onChange(next);
              }}
            >
              {MOD_SOURCES.map(s => (
                <option key={s} value={s}>{s === "lfo" ? "lfo1" : s}</option>
              ))}
            </select>
            <select
              className={authorRouteControl}
              value={route.target}
              onChange={event => {
                const next = routes.map((r, i) =>
                  i === index ? { ...r, target: event.target.value as SamplerModTarget } : r,
                );
                onChange(next);
              }}
            >
              {MOD_TARGETS.map(t => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <ParamKnob
              min={-2}
              max={2}
              step={0.01}
              value={route.depth}
              defaultValue={1}
              density="compact"
              size={28}
              valuePlacement="end"
              ariaLabel={`Route ${index + 1} depth`}
              learnPath={instrumentPresetLearnPath(slot, `routes.${index}.depth`)}
              onChange={depth => {
                const next = routes.map((r, i) => (i === index ? { ...r, depth } : r));
                onChange(next);
              }}
            />
            <label className="flex items-center gap-[0.28rem] text-[10px] text-lab-muted">
              <Checkbox
                checked={Boolean(route.invert)}
                aria-label={`Route ${index + 1} invert`}
                onCheckedChange={invert => {
                  const next = routes.map((r, i) =>
                    i === index ? { ...r, invert: invert || undefined } : r,
                  );
                  onChange(next);
                }}
              />
              inv
            </label>
            <button
              type="button"
              title="Remove route"
              onClick={() => onChange(routes.filter((_, i) => i !== index))}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

const authorRouteControl =
  "h-7 rounded border border-white/12 bg-white/[0.04] px-[0.3rem] text-[11px] text-slate-200 [font:inherit]";
